import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { config } from '../config';
import PaymentSuccess from './PaymentSuccess';
import './StripePayment.css';

// Initialize Stripe
console.log('🔑 Stripe publishable key:', config.STRIPE_PUBLISHABLE_KEY);
const stripePromise = loadStripe(config.STRIPE_PUBLISHABLE_KEY);

interface PaymentFormProps {
  amount: number;
  description: string;
  metadata?: Record<string, any>;
  onSuccess: (paymentIntent: any) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
}

interface PaymentIntent {
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  description,
  metadata,
  onSuccess,
  onError,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Create payment intent when component mounts
    createPaymentIntent();
  }, []);

  console.log('🔍 CheckoutForm render - clientSecret:', clientSecret, 'loading:', loading, 'error:', error);

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      console.log('🚀 NEW STRIPE COMPONENT LOADED - Creating payment intent with:', { amount, description, metadata });
      console.log('🔗 Backend URL:', config.BACKEND_URL);
      console.log('⏰ Timestamp:', new Date().toISOString());
      
      const response = await axios.post(`${config.BACKEND_URL}/api/stripe/create-payment-intent-simple?cache_bust=${Date.now()}`, {
        amount,
        description,
        metadata,
        debug: "NEW_ENDPOINT_CALLED"
      });

      console.log('Payment intent response:', response.data);

      if (response.data.success) {
        console.log('🔑 Setting client secret:', response.data.client_secret);
        setClientSecret(response.data.client_secret);
      } else {
        console.log('❌ Payment intent creation failed');
        onError('Failed to create payment intent');
      }
    } catch (err: any) {
      console.error('Payment intent error:', err);
      onError(err.response?.data?.detail || 'Failed to create payment intent');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      setLoading(false);
      return;
    }

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      }
    });

    if (stripeError) {
      setError(stripeError.message || 'Payment failed');
      setLoading(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      console.log('🎉 Payment succeeded in PaymentForm:', paymentIntent);
      onSuccess(paymentIntent);
    }
  };

  if (loading && !clientSecret) {
    return (
      <div className="payment-loading">
        <div className="spinner"></div>
        <p>Setting up payment...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="payment-details">
        <h3>Payment Details</h3>
        <p><strong>Amount:</strong> ${amount.toFixed(2)} CAD</p>
        <p><strong>Description:</strong> {description}</p>
      </div>

      <div className="card-element-container">
        <label>Card Information</label>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
            hidePostalCode: true
          }}
        />
      </div>

      <div className="postal-code-container">
        <label>Postal Code (Canadian Format)</label>
        <input
          type="text"
          placeholder="K1A 0A6"
          pattern="[A-Za-z][0-9][A-Za-z] [0-9][A-Za-z][0-9]"
          title="Enter a valid Canadian postal code (e.g., K1A 0A6)"
          className="postal-code-input"
          required
        />
        <small className="postal-code-help">Format: A1A 1A1 (letters and numbers)</small>
      </div>

      {error && (
        <div className="payment-error">
          {error}
        </div>
      )}

      <div className="payment-actions">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!stripe || loading}
        >
          {loading ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
};

interface StripePaymentProps {
  amount: number;
  description: string;
  metadata?: Record<string, any>;
  interacContext?: {
    invoiceNumber?: string;
    eventName?: string;
    studentName?: string;
    messageOverride?: string;
  };
  onSuccess: (paymentIntent: any) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
  onInteracSelected?: () => void;
  show: boolean;
  onBackToDashboard?: () => void;
  paymentSuccess?: any;
}

const StripePayment: React.FC<StripePaymentProps> = ({
  amount,
  description,
  metadata,
  interacContext,
  onSuccess,
  onError,
  onCancel,
  onInteracSelected,
  show,
  onBackToDashboard,
  paymentSuccess: propPaymentSuccess
}) => {
  const [internalPaymentSuccess, setInternalPaymentSuccess] = useState<any>(null);
  const [hasShownSuccess, setHasShownSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'choice' | 'interac' | 'stripe'>('choice');
  
  // Use prop paymentSuccess if available, otherwise use internal state
  const paymentSuccess = propPaymentSuccess || internalPaymentSuccess;
  
  console.log('🔄 StripePayment render - show:', show, 'propPaymentSuccess:', propPaymentSuccess, 'internalPaymentSuccess:', internalPaymentSuccess, 'final paymentSuccess:', paymentSuccess);
  console.log('🔄 paymentSuccess type:', typeof paymentSuccess, 'is null:', paymentSuccess === null, 'is undefined:', paymentSuccess === undefined);
  console.log('🔄 paymentSuccess truthy:', !!paymentSuccess);

  // Track component lifecycle
  useEffect(() => {
    console.log('🟢 StripePayment Mounted');
    return () => {
      console.log('🔴 StripePayment Unmounted');
    };
  }, []);

  // Track paymentSuccess state changes
  useEffect(() => {
    console.log('✨ StripePayment useEffect - paymentSuccess changed to:', paymentSuccess);
    console.log('✨ paymentSuccess type:', typeof paymentSuccess, 'is null:', paymentSuccess === null, 'is undefined:', paymentSuccess === undefined);
    console.log('✨ paymentSuccess truthy:', !!paymentSuccess);
  }, [paymentSuccess]);

  useEffect(() => {
    if (show) {
      setPaymentMethod('choice');
    }
  }, [show]);

  const handlePaymentSuccess = (paymentIntent: any) => {
    console.log('🎉 handlePaymentSuccess called with:', paymentIntent);
    console.log('🎉 About to call setInternalPaymentSuccess with:', paymentIntent);
    setInternalPaymentSuccess(paymentIntent);
    setHasShownSuccess(true);
    console.log('🎉 setInternalPaymentSuccess called, component should re-render');
    onSuccess(paymentIntent);
  };

  const handleBackToDashboard = () => {
    console.log('🏠 handleBackToDashboard called');
    setInternalPaymentSuccess(null);
    if (onBackToDashboard) {
      onBackToDashboard();
    }
  };

  const handleMakeAnotherPayment = () => {
    setInternalPaymentSuccess(null);
    setHasShownSuccess(false);
  };

  // Show payment success screen FIRST (even if show is false, we want to show success)
  console.log('🔍 Checking paymentSuccess condition - paymentSuccess:', paymentSuccess, 'truthy:', !!paymentSuccess, 'hasShownSuccess:', hasShownSuccess);
  if (paymentSuccess || hasShownSuccess) {
    console.log('🎉 Rendering PaymentSuccess component with:', paymentSuccess);
    return (
      <div className="stripe-payment-modal">
        <PaymentSuccess
          paymentIntent={paymentSuccess}
          onBackToDashboard={handleBackToDashboard}
          onMakeAnotherPayment={handleMakeAnotherPayment}
        />
      </div>
    );
  } else {
    console.log('❌ paymentSuccess is falsy, not rendering success page');
  }

  // Only return null if show is false AND we don't have a successful payment
  if (!show) return null;

  const eventName = interacContext?.eventName || description;
  const studentName = interacContext?.studentName || (metadata?.student_name as string) || 'Student';
  const invoiceToken = interacContext?.invoiceNumber
    || (metadata?.invoice_number as string)
    || (metadata?.invoice_id ? `INV-${String(metadata.invoice_id).slice(0, 8).toUpperCase()}` : '');
  const interacMessage = interacContext?.messageOverride
    || [invoiceToken, eventName, studentName].filter(Boolean).join(' | ');

  const handleInteracDone = () => {
    if (onInteracSelected) {
      onInteracSelected();
    }
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="stripe-payment-modal">
      <div className="stripe-payment-content">
        <div className="stripe-payment-header">
          <h2>💳 Secure Payment</h2>
          <p>Powered by Stripe</p>
        </div>

        {paymentMethod === 'choice' && (
          <div className="payment-form" style={{ padding: '8px 0' }}>
            <h3>Choose Payment Method</h3>
            <p style={{ marginBottom: '14px', color: '#555' }}>
              As a not-for-profit society, we prefer Interac e-transfer to reduce processing fees.
            </p>
            <div className="payment-actions" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setPaymentMethod('interac')}
              >
                Interac e-Transfer (Recommended)
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPaymentMethod('stripe')}
              >
                Pay by Credit / Debit Card (Stripe)
              </button>
            </div>
            {onCancel && (
              <div className="payment-actions" style={{ justifyContent: 'center', marginTop: '10px' }}>
                <button type="button" className="btn btn-secondary" onClick={onCancel}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {paymentMethod === 'interac' && (
          <div className="payment-form">
            <h3>Interac e-Transfer Instructions</h3>
            <p><strong>E-Transfer Email:</strong> admin@oarayouthinnovationlab.ca</p>
            <p style={{ color: '#555', marginBottom: '8px' }}>
              Interac transfer settlement can take several hours. Your pending invoice will remain open until payment is received.
            </p>
            <div className="payment-details">
              <p><strong>Amount:</strong> ${amount.toFixed(2)} CAD</p>
              <p><strong>Interac Message (copy/paste):</strong></p>
              <code style={{ display: 'block', background: '#f5f5f5', padding: '8px', borderRadius: '6px' }}>
                {interacMessage}
              </code>
            </div>
            <div className="payment-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setPaymentMethod('choice')}>
                Back
              </button>
              <button type="button" className="btn btn-primary" onClick={handleInteracDone}>
                I Will Pay by Interac
              </button>
            </div>
          </div>
        )}

        {paymentMethod === 'stripe' && (
          <Elements stripe={stripePromise} options={{
            locale: 'en-CA'
          }}>
            <PaymentForm
              amount={amount}
              description={description}
              metadata={metadata}
              onSuccess={handlePaymentSuccess}
              onError={onError}
              onCancel={() => setPaymentMethod('choice')}
            />
          </Elements>
        )}

        <div className="stripe-payment-footer">
          <div className="security-badges">
            <span className="security-badge">🔒 SSL Secured</span>
            <span className="security-badge">🛡️ PCI Compliant</span>
            <span className="security-badge">✅ Stripe Protected</span>
          </div>
          <p className="stripe-disclaimer">
            Your payment information is processed securely by Stripe. 
            We never store your card details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StripePayment;
